import { classNameJoin, getErrorData } from 'utils/lib'
import Input from './Input'
import Button from './Button'
import { IMe } from 'interfaces/user.interface'
import { useState, useCallback } from 'react'
import { patchUsersMe } from 'api/user.request'
import { useConfirmV2 } from 'contexts/ConfirmV2Provider'

interface Props {
    user: IMe | null
    onClose: () => void
}

interface BusinessInfo {
    businessName: string
    representativeName: string
    businessRegistrationNumber: string
    mailOrderSalesRegistrationNumber: string
    businessAddress: string
    businessPhone: string
}

const BusinessInfoInputModal = (props: Props) => {
    const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
        businessName: props.user?.businessInfo?.businessName || '',
        representativeName: props.user?.businessInfo?.representativeName || '',
        businessRegistrationNumber: props.user?.businessInfo?.businessRegistrationNumber || '',
        mailOrderSalesRegistrationNumber:
            props.user?.businessInfo?.mailOrderSalesRegistrationNumber || '',
        businessAddress: props.user?.businessInfo?.businessAddress || '',
        businessPhone: props.user?.businessInfo?.businessPhone || '',
    })

    const [errors, setErrors] = useState<Partial<BusinessInfo>>({})
    const [isLoading, setIsLoading] = useState(false)
    const [serverError, setServerError] = useState<string>('')

    const validateForm = useCallback(() => {
        const newErrors: Partial<BusinessInfo> = {}

        if (!businessInfo.businessName) newErrors.businessName = '상호명을 입력해주세요'
        if (!businessInfo.representativeName)
            newErrors.representativeName = '대표자명을 입력해주세요'
        if (!businessInfo.businessRegistrationNumber)
            newErrors.businessRegistrationNumber = '사업자등록번호를 입력해주세요'
        if (!businessInfo.mailOrderSalesRegistrationNumber)
            newErrors.mailOrderSalesRegistrationNumber = '통신판매업번호를 입력해주세요'
        if (!businessInfo.businessAddress) newErrors.businessAddress = '사업자주소를 입력해주세요'
        if (!businessInfo.businessPhone)
            newErrors.businessPhone = '고객센터 전화번호를 입력해주세요'

        if (
            businessInfo.businessRegistrationNumber &&
            !/^\d+$/.test(businessInfo.businessRegistrationNumber)
        ) {
            newErrors.businessRegistrationNumber = '숫자만 입력 가능합니다'
        }
        if (
            businessInfo.businessRegistrationNumber &&
            businessInfo.businessRegistrationNumber.length > 10
        ) {
            newErrors.businessRegistrationNumber = '10자리 이하로 입력해주세요'
        }

        if (businessInfo.businessPhone && !/^\d+$/.test(businessInfo.businessPhone)) {
            newErrors.businessPhone = '숫자만 입력 가능합니다'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [businessInfo])

    const onSubmitHandler = useCallback(async () => {
        if (validateForm()) {
            setIsLoading(true)
            setServerError('')
            try {
                await patchUsersMe({
                    businessName: businessInfo.businessName,
                    representativeName: businessInfo.representativeName,
                    businessRegistrationNumber: businessInfo.businessRegistrationNumber,
                    mailOrderSalesRegistrationNumber: businessInfo.mailOrderSalesRegistrationNumber,
                    businessAddress: businessInfo.businessAddress,
                    businessPhone: businessInfo.businessPhone,
                })
                // 완료되면 새로고침 한번!
                window.location.reload()
                props.onClose()
            } catch (error: any) {
                const err = getErrorData(error)
                if (err) {
                    setServerError(
                        `오류가 발생했습니다. 다시 시도해주세요. - ${err.code ?? err.message}`
                    )
                } else {
                    setServerError(`오류가 발생했습니다. 다시 시도해주세요.`)
                }
            } finally {
                setIsLoading(false)
            }
        }
    }, [businessInfo, validateForm, props])

    const handleInputChange = useCallback((field: keyof BusinessInfo, value: string) => {
        if (field === 'businessRegistrationNumber') {
            // 숫자만 입력 가능하고 10자리로 제한
            const numericValue = value.replace(/[^0-9]/g, '')
            if (numericValue.length <= 10) {
                setBusinessInfo((prev) => ({
                    ...prev,
                    [field]: numericValue,
                }))
            }
        } else if (field === 'businessPhone') {
            // 숫자만 입력 가능
            const numericValue = value.replace(/[^0-9]/g, '')
            setBusinessInfo((prev) => ({
                ...prev,
                [field]: numericValue,
            }))
        } else {
            setBusinessInfo((prev) => ({
                ...prev,
                [field]: value,
            }))
        }
    }, [])

    const handleFocus = useCallback((field: keyof BusinessInfo) => {
        setErrors((prev) => ({
            ...prev,
            [field]: undefined,
        }))
    }, [])

    return (
        <div className="w-screen h-[100dvh] md:h-screen fixed z-[999999] top-0 left-0 bg-black/20 flex items-center justify-center">
            <article
                style={{
                    scrollbarWidth: 'none',
                }}
                className={classNameJoin([
                    'md:max-w-[95vw] md:max-h-[90vh]',
                    'w-[100vw] md:w-[520px]',
                    'h-[100dvh] md:h-fit',
                    'overflow-y-scroll',
                    'bg-white',
                    'md:rounded-[20px]',
                    'p-5',
                    'flex flex-col gap-7',
                    'relative',
                    'animate-fade-in',
                ])}
            >
                {isLoading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-[20px] z-10">
                        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    <p className="text-lg font-medium">사업자 정보를 입력해주세요.</p>
                    <p className="text-[12px] text-[#898989] break-keep ">
                        「전자상거래 등에서의 소비자보호에 관한 법률」 제 10조 1항에 따라,
                        통신판매업자는 소비자가 확인할 수 있도록 사업자정보를 표시해야 합니다.
                        입력된 정보는 셀러 페이지 하단에 노출됩니다.
                    </p>
                </div>
                <div className="flex flex-col gap-5">
                    <LabelWithInputWrap>
                        <Label label="상호명" />
                        <div className="flex flex-col gap-1">
                            <Input
                                placeholder="상호명 입력"
                                value={businessInfo.businessName}
                                onChange={(e) => handleInputChange('businessName', e.target.value)}
                                onFocus={() => handleFocus('businessName')}
                                disabled={!!props.user?.businessInfo?.businessName || isLoading}
                            />
                            {errors.businessName && (
                                <p className="text-xs text-red-500 pl-1 mt-1">
                                    {errors.businessName}
                                </p>
                            )}
                        </div>
                    </LabelWithInputWrap>
                    <LabelWithInputWrap>
                        <Label label="대표자명" />
                        <div className="flex flex-col gap-1">
                            <Input
                                placeholder="대표자명 입력"
                                value={businessInfo.representativeName}
                                onChange={(e) =>
                                    handleInputChange('representativeName', e.target.value)
                                }
                                onFocus={() => handleFocus('representativeName')}
                                disabled={
                                    !!props.user?.businessInfo?.representativeName || isLoading
                                }
                            />
                            {errors.representativeName && (
                                <p className="text-xs text-red-500 pl-1 mt-1">
                                    {errors.representativeName}
                                </p>
                            )}
                        </div>
                    </LabelWithInputWrap>
                    <LabelWithInputWrap>
                        <Label label="사업자등록번호" />
                        <div className="flex flex-col gap-1">
                            <Input
                                placeholder="- 없이 숫자만 입력"
                                value={businessInfo.businessRegistrationNumber}
                                onChange={(e) =>
                                    handleInputChange('businessRegistrationNumber', e.target.value)
                                }
                                onFocus={() => handleFocus('businessRegistrationNumber')}
                                disabled={
                                    !!props.user?.businessInfo?.businessRegistrationNumber ||
                                    isLoading
                                }
                            />
                            {errors.businessRegistrationNumber && (
                                <p className="text-xs text-red-500 pl-1 mt-1">
                                    {errors.businessRegistrationNumber}
                                </p>
                            )}
                        </div>
                    </LabelWithInputWrap>
                    <LabelWithInputWrap>
                        <Label
                            label="통신판매업번호"
                            sub="(간이과세사업자일 경우 ‘간이과세자’ 로 입력해주세요)"
                        />
                        <div className="flex flex-col gap-1">
                            <Input
                                placeholder="통신판매업번호 입력"
                                value={businessInfo.mailOrderSalesRegistrationNumber}
                                onChange={(e) =>
                                    handleInputChange(
                                        'mailOrderSalesRegistrationNumber',
                                        e.target.value
                                    )
                                }
                                onFocus={() => handleFocus('mailOrderSalesRegistrationNumber')}
                                disabled={
                                    !!props.user?.businessInfo?.mailOrderSalesRegistrationNumber ||
                                    isLoading
                                }
                            />
                            {errors.mailOrderSalesRegistrationNumber && (
                                <p className="text-xs text-red-500 pl-1 mt-1">
                                    {errors.mailOrderSalesRegistrationNumber}
                                </p>
                            )}
                        </div>
                    </LabelWithInputWrap>
                    <LabelWithInputWrap>
                        <Label label="사업자주소" />
                        <div className="flex flex-col gap-1">
                            <Input
                                placeholder="사업자주소 입력"
                                value={businessInfo.businessAddress}
                                onChange={(e) =>
                                    handleInputChange('businessAddress', e.target.value)
                                }
                                onFocus={() => handleFocus('businessAddress')}
                                disabled={!!props.user?.businessInfo?.businessAddress || isLoading}
                            />
                            {errors.businessAddress && (
                                <p className="text-xs text-red-500 pl-1 mt-1">
                                    {errors.businessAddress}
                                </p>
                            )}
                        </div>
                    </LabelWithInputWrap>
                    <LabelWithInputWrap>
                        <Label label="고객센터 전화번호" />
                        <div className="flex flex-col gap-1">
                            <Input
                                placeholder="- 없이 숫자만 입력"
                                value={businessInfo.businessPhone}
                                onChange={(e) => handleInputChange('businessPhone', e.target.value)}
                                onFocus={() => handleFocus('businessPhone')}
                                disabled={!!props.user?.businessInfo?.businessPhone || isLoading}
                            />
                            {errors.businessPhone && (
                                <p className="text-xs text-red-500 pl-1 mt-1">
                                    {errors.businessPhone}
                                </p>
                            )}
                        </div>
                    </LabelWithInputWrap>
                </div>
                {serverError && <p className="text-sm text-red-500 text-center">{serverError}</p>}
                <div className="flex justify-center items-center gap-2">
                    <Button small ghost onClick={props.onClose} disabled={isLoading}>
                        닫기
                    </Button>
                    <Button small onClick={onSubmitHandler} disabled={isLoading}>
                        저장하기
                    </Button>
                </div>
            </article>
        </div>
    )
}

const Label = ({ label, sub }: { label: string; sub?: string }) => {
    return (
        <div className="flex flex-row items-center gap-1">
            <p className="text-[14px] text-[#242424]">{label}</p>
            {sub && <p className="text-[14px] text-[#898989]">{sub}</p>}
        </div>
    )
}
const LabelWithInputWrap = ({ children }: { children: React.ReactNode }) => {
    return <div className="flex flex-col gap-2">{children}</div>
}

export default BusinessInfoInputModal
